/**
 * phyllotaxisPipeline — Fibonacci sunflower spirals.
 *
 * Points placed at the golden angle (137.5°) form the iconic sunflower
 * pattern. We place N=800 points and render them as glowing dots.
 * Each point's size and color is modulated by its distance from center
 * (younger seeds near the center are smaller). Audio drives point
 * density and color palette.
 *
 * The phyllotaxis pattern is found throughout nature — sunflower
 * seeds, pine cones, pineapples, daisies. It maximizes packing
 * efficiency and is mathematically related to the golden ratio.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PhyllotaxisPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// Returns (x, y, size) for the i-th seed.
vec3 seedPos(float i) {
  const float GOLDEN_ANGLE = 2.39996323;  // radians (137.5 deg)
  float r = sqrt(i) * 0.018;  // radius proportional to sqrt(i) — gives uniform density
  float theta = i * GOLDEN_ANGLE;
  return vec3(cos(theta) * r, sin(theta) * r, 1.0 / (1.0 + i * 0.05));
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Slow rotation (audio drives speed).
  float angle = uTime * 0.15 * (0.5 + bass * 1.0);
  float ca = cos(angle), sa = sin(angle);
  mat2 rot = mat2(ca, -sa, sa, ca);

  // Background: deep dark with subtle radial vignette.
  vec3 col = vec3(0.04, 0.03, 0.08);
  float dist = length(p);
  col *= 1.0 - smoothstep(0.4, 1.0, dist) * 0.5;

  // Number of seeds scales with audio.
  float N = 600.0 + bass * 400.0;

  // Accumulate seed glow.
  vec3 accumGlow = vec3(0.0);
  // Loop over seeds. We use a fixed upper bound for the loop, and
  // break early when the seed index exceeds N.
  const int MAX_SEEDS = 1000;
  for (int i = 0; i < MAX_SEEDS; i++) {
    if (float(i) >= N) break;
    vec3 s = seedPos(float(i));
    // Apply rotation.
    vec2 sp = rot * s.xy;
    // Audio pulse on each seed.
    float pulse = 0.7 + 0.3 * sin(uTime * 2.0 + float(i) * 0.05);
    float radius = s.z * 0.012 * pulse;
    float d = length(p - sp);
    float glow = exp(-d * d / (radius * radius)) * 0.04;
    // Color: rainbow by seed index with palette shift over time.
    float hue = float(i) / N + uTime * 0.05;
    vec3 seedColor = vec3(
      0.5 + 0.5 * cos(hue * 6.2831853),
      0.5 + 0.5 * cos(hue * 6.2831853 + 2.094),
      0.5 + 0.5 * cos(hue * 6.2831853 + 4.189)
    );
    // Brighter for outer seeds (more saturated).
    seedColor = mix(seedColor, vec3(1.0, 0.8, 0.4), float(i) / N * 0.5);
    // Audio: brighten with high frequencies.
    seedColor *= 1.0 + high * 0.6;
    accumGlow += seedColor * glow;
  }
  col += accumGlow;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computePhyllotaxisBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computePhyllotaxisRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPhyllotaxisPipeline(gl: WebGL2RenderingContext): PhyllotaxisPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPhyllotaxis(
  gl: WebGL2RenderingContext,
  pipe: PhyllotaxisPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
